import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

const TOKEN = {
  success: { Icon: CheckCircleOutlineIcon, iconColor: '#15803d', badgeBg: 'rgba(21,128,61,0.10)',  bg: 'rgba(240,253,244,0.94)', border: 'rgba(134,239,172,0.5)', dot: '#16a34a' },
  error:   { Icon: ErrorOutlineIcon,       iconColor: '#dc2626', badgeBg: 'rgba(220,38,38,0.10)',   bg: 'rgba(254,242,242,0.94)', border: 'rgba(252,165,165,0.5)', dot: '#ef4444' },
  warning: { Icon: WarningAmberIcon,       iconColor: '#d97706', badgeBg: 'rgba(217,119,6,0.10)',   bg: 'rgba(255,251,235,0.94)', border: 'rgba(253,211,77,0.5)',  dot: '#f59e0b' },
  info:    { Icon: InfoOutlinedIcon,       iconColor: '#1e3a5f', badgeBg: 'rgba(83,131,179,0.10)',  bg: 'rgba(239,246,255,0.94)', border: 'rgba(147,197,253,0.5)', dot: '#5383b3' },
};

function ToastItem({ item, onRemove }) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);
  const t = TOKEN[item.severity] ?? TOKEN.info;
  const { Icon } = t;

  const dismiss = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    clearTimeout(timerRef.current);
    setTimeout(() => onRemove(item.id), 260);
  }, [leaving, item.id, onRemove]);

  useEffect(() => {
    timerRef.current = setTimeout(dismiss, item.duration);
    return () => clearTimeout(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        px: '16px',
        py: '13px',
        minWidth: { xs: 0, sm: 270 },
        maxWidth: { xs: '100%', sm: 400 },
        width: { xs: '100%', sm: 'auto' },
        borderRadius: '12px',
        bgcolor: t.bg,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        border: `1px solid ${t.border}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        pointerEvents: 'all',
        willChange: 'transform, opacity',
        animation: leaving
          ? 'toastOut 0.26s cubic-bezier(0.55,0,1,0.45) both'
          : 'toastIn 0.34s cubic-bezier(0.22,1,0.36,1) both',
        '@keyframes toastIn': {
          from: { opacity: 0, transform: 'translateX(32px) scale(0.97)' },
          to:   { opacity: 1, transform: 'translateX(0)   scale(1)'    },
        },
        '@keyframes toastOut': {
          from: { opacity: 1, transform: 'translateX(0)   scale(1)'    },
          to:   { opacity: 0, transform: 'translateX(28px) scale(0.96)' },
        },
      }}
    >
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: t.badgeBg, color: t.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon sx={{ fontSize: 15 }} />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b', lineHeight: 1.45, wordBreak: 'break-word' }}>
          {item.message}
        </Typography>
        <Box sx={{ height: 2, borderRadius: '99px', mt: '6px', bgcolor: t.dot, transformOrigin: 'left center', animation: `toastProgress ${item.duration}ms linear both`, '@keyframes toastProgress': { from: { transform: 'scaleX(1)', opacity: 0.7 }, to: { transform: 'scaleX(0)', opacity: 0.3 } } }} />
      </Box>

      <IconButton size="small" onClick={dismiss} sx={{ width: 22, height: 22, borderRadius: '50%', color: '#94a3b8', p: 0, ml: '2px', flexShrink: 0, transition: 'background 0.15s, color 0.15s', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: '#475569' }, '&:active': { bgcolor: 'rgba(0,0,0,0.10)' } }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Paper>
  );
}

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message, severity = 'info', duration = 4000) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev.slice(-4), { id, message, severity, duration }]);
  }, []);

  const toast = useMemo(() => ({
    success: (msg, dur = 4000) => show(msg, 'success', dur),
    error:   (msg, dur = 6000) => show(msg, 'error',   dur),
    warning: (msg, dur = 5000) => show(msg, 'warning', dur),
    info:    (msg, dur = 4000) => show(msg, 'info',    dur),
  }), [show]);

  useEffect(() => {
    const handler = (e) => show(e.detail.message, 'error', 6000);
    window.addEventListener('api-error', handler);
    return () => window.removeEventListener('api-error', handler);
  }, [show]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Box sx={{ position: 'fixed', bottom: { xs: 16, sm: 28 }, right: { xs: 12, sm: 28 }, left: { xs: 12, sm: 'auto' }, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px', alignItems: { xs: 'stretch', sm: 'flex-end' }, pointerEvents: 'none' }}>
        {toasts.map((item) => (
          <ToastItem key={item.id} item={item} onRemove={remove} />
        ))}
      </Box>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
